import { debounce } from '@/core'
import { CourseSearchArgs, CourseSearchFormValues } from '@/features/course'
import { useTypedDispatch } from '@/store'
import { useCallback, useEffect, useMemo } from 'react'
import { parseTemplate } from 'url-template'
import { courseCatalogApi as tenantApi } from '@/services/api/tenant/courseCatalogEnhanced'
import { useCourseSearchFormContext } from './useCourseSearchFormContext'

const searchQs = parseTemplate('{name}')

export const useSearchCoursesArgs = (
  values?: CourseSearchFormValues,
  page?: number,
  size?: number,
): CourseSearchArgs => {
  return useMemo<CourseSearchArgs>(() => {
    const queryObject: CourseSearchArgs = {
      query: decodeURIComponent(
        searchQs.expand({
          name: (values?.name && `${values?.name}`) || '',
        }),
      ).trim(),
    }
    if (page !== undefined) {
      queryObject.page = page
    }
    if (size !== undefined) {
      queryObject.size = size
    }
    return queryObject
  }, [values, page, size])
}

export const useSearchCoursesState = (values: CourseSearchFormValues, page?: number, size?: number) => {
  const searchArgs = useSearchCoursesArgs(values, page, size)

  const { data, isFetching, isUninitialized, refetch } = tenantApi.endpoints.listCourses.useQuery(searchArgs)
  return { data, isFetching, isUninitialized, refetch }
}

export const useSearchCourses = (page?: number, size?: number) => {
  const dispatch = useTypedDispatch()
  const { values } = useCourseSearchFormContext()
  const searchArgs = useSearchCoursesArgs(values, page, size)

  const tenantSearchFn = useCallback(
    (args: typeof searchArgs) => {
      dispatch(tenantApi.endpoints.listCourses.initiate(args))
    },
    [dispatch],
  )
  const debouncedCourseSearchFn = useMemo(
    () =>
      debounce((args: typeof searchArgs) => {
        tenantSearchFn(args)
      }, 100),

    [tenantSearchFn],
  )

  useEffect(() => {
    tenantSearchFn(searchArgs)
  }, [])

  useEffect(() => {
    debouncedCourseSearchFn(searchArgs)
  }, [searchArgs, debouncedCourseSearchFn])

  return useSearchCoursesState(values, page, size)
}
